import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShieldCheck } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = [{
  title: '1. Información que Recopilamos',
  body: 'Recopilamos únicamente la información necesaria para procesar tu compra: nombre, dirección de correo electrónico y datos de pago. No almacenamos información de tarjetas de crédito; todos los pagos son procesados de forma segura por nuestro proveedor de pagos certificado.'
}, {
  title: '2. Uso de la Información',
  body: 'Tu información personal se utiliza exclusivamente para: procesar y entregar tu compra, enviarte el producto digital adquirido, y comunicarnos contigo sobre actualizaciones relacionadas con tu compra. No vendemos, alquilamos ni compartimos tu información personal con terceros.'
}, {
  title: '3. Protección de Datos',
  body: 'Implementamos medidas de seguridad estándar de la industria para proteger tu información personal, incluyendo encriptación SSL/TLS en todas las transmisiones de datos y almacenamiento seguro de información.'
}, {
  title: '4. Cookies',
  body: 'Utilizamos cookies esenciales para el funcionamiento del sitio y cookies analíticas para mejorar tu experiencia. Puedes configurar tu navegador para rechazar cookies, aunque esto podría afectar la funcionalidad del sitio.'
}, {
  title: '5. Tus Derechos',
  body: 'Tienes derecho a acceder, rectificar o eliminar tu información personal en cualquier momento. Para ejercer estos derechos, puedes contactarnos a través de los canales indicados en tu correo de confirmación de compra.'
}];
const termsContent = [{
  title: '1. Producto Digital',
  body: 'El Manual del Empleado Invisible es un producto digital (PDF y materiales complementarios) que se entrega de forma instantánea tras la confirmación del pago. Al realizar la compra, obtienes una licencia personal e intransferible para usar el contenido.'
}, {
  title: '2. Uso Permitido',
  body: 'El contenido es para tu uso personal y profesional. No está permitido redistribuir, revender, compartir públicamente o reproducir el material, total o parcialmente, sin autorización expresa por escrito.'
}, {
  title: '3. Política de Reembolso',
  body: 'Debido a la naturaleza digital del producto y su entrega instantánea, todas las ventas son finales. Sin embargo, si experimentas problemas técnicos para acceder al material, nos comprometemos a resolverlos de manera oportuna.'
}, {
  title: '4. Limitación de Responsabilidad',
  body: 'Los resultados pueden variar según el uso individual. El manual proporciona herramientas y metodologías, pero no garantiza resultados específicos. El usuario es responsable de revisar y validar cualquier contenido generado con asistencia de IA.'
}, {
  title: '5. Propiedad Intelectual',
  body: 'Todo el contenido, incluyendo textos, diseños, metodologías y materiales complementarios, está protegido por derechos de autor. El uso no autorizado constituye una violación de la ley de propiedad intelectual aplicable.'
}, {
  title: '6. Modificaciones',
  body: 'Nos reservamos el derecho de actualizar estos términos en cualquier momento. Las modificaciones serán efectivas desde su publicación en este sitio. El uso continuado del producto implica la aceptación de los términos actualizados.'
}];
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const closeModal = () => setActiveModal(null);
  return <>
      <footer className="py-10 border-t border-slate-800/50 bg-[#0a0f1a]" data-id="element-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-25">
          <div className="flex flex-col items-center gap-5" data-id="element-26">
            {/* Legal Links */}
            <div className="flex items-center gap-6" data-id="element-27">
              <button onClick={() => setActiveModal('privacy')} className="text-slate-400 hover:text-cyan-400 text-sm transition-colors duration-200" data-id="element-28">
                Política de Privacidad
              </button>
              <span className="text-slate-700" data-id="element-29">|</span>
              <button onClick={() => setActiveModal('terms')} className="text-slate-400 hover:text-cyan-400 text-sm transition-colors duration-200" data-id="element-30">
                Términos y Condiciones
              </button>
            </div>

            {/* Trust badge */}
            <div className="flex items-center gap-2 text-slate-500 text-xs" data-id="element-31">
              <ShieldCheck className="w-4 h-4 text-green-500/70" data-id="element-32" />
              <span data-id="element-33">Pago seguro y encriptado</span>
            </div>

            {/* Copyright */}
            <p className="text-slate-600 text-xs" data-id="element-34">
              © {new Date().getFullYear()} El Manual del Empleado Invisible.
              Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>

      {/* Modal */}
      <AnimatePresence data-id="element-35">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={closeModal} data-id="element-36">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" data-id="element-37" />

            {/* Modal Content */}
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25,
          ease: [0.21, 0.47, 0.32, 0.98]
        }} onClick={e => e.stopPropagation()} className="relative bg-[#111827] border border-slate-700/80 rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden shadow-2xl shadow-black/50" data-id="element-38">
              {/* Header */}
              <div className="sticky top-0 z-10 bg-[#111827] border-b border-slate-800 px-6 py-5 flex items-center justify-between" data-id="element-39">
                <h3 className="text-xl font-bold text-white" data-id="element-40">
                  {activeModal === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
                </h3>
                <button onClick={closeModal} className="text-slate-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-slate-800" data-id="element-41">
                  <X className="w-5 h-5" data-id="element-42" />
                </button>
              </div>

              {/* Scrollable Body */}
              <div className="px-6 py-6 overflow-y-auto max-h-[calc(80vh-72px)] space-y-6" data-id="element-43">
                <p className="text-slate-400 text-sm" data-id="element-44">
                  Última actualización:{' '}
                  {new Date().toLocaleDateString('es-ES', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
                </p>

                {(activeModal === 'privacy' ? privacyContent : termsContent).map((section, i) => <div key={i} data-id="element-45">
                    <h4 className="text-white font-semibold text-base mb-2" data-id="element-46">
                      {section.title}
                    </h4>
                    <p className="text-[#d1d5db] text-sm leading-relaxed" data-id="element-47">
                      {section.body}
                    </p>
                  </div>)}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}